import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Headphones, Send, Mail, Clock, CheckCircle2, MessageSquare, Plus, ArrowLeft, Inbox, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalSupport = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedThread, setSelectedThread] = useState(null);
  const [formData, setFormData] = useState({ subject: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/portal/messages`, { withCredentials: true });
      setMessages(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.content.trim()) {
      toast.error('Please provide a subject and message');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/messages`, {
        subject: formData.subject,
        content: formData.content,
        recipient_id: null, // null = to membership team
      }, { withCredentials: true });
      toast.success('Message sent to the Membership Team!');
      setFormData({ subject: '', content: '' });
      setView('list');
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const markAsRead = async (msgId) => {
    try {
      await axios.put(`${API}/portal/messages/${msgId}/read`, {}, { withCredentials: true });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, is_read: true } : m));
    } catch (err) {
      // error handled silently
    }
  };

  // Group messages into threads by subject
  const threads = messages.reduce((acc, msg) => {
    const subject = msg.subject?.replace(/^Re: /, '') || 'No subject';
    if (!acc[subject]) {
      acc[subject] = [];
    }
    acc[subject].push(msg);
    return acc;
  }, {});

  const threadList = Object.entries(threads).map(([subject, msgs]) => ({
    subject,
    messages: msgs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    lastMessage: msgs[msgs.length - 1],
    hasUnread: msgs.some(m => !m.is_read && m.recipient_id === user?.id),
  })).sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));

  const sentMessages = messages.filter(m => m.sender_id === user?.id && m.is_support_request);
  const receivedMessages = messages.filter(m => m.recipient_id === user?.id);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portal-support">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-[#E63946] rounded-sm flex items-center justify-center">
            <Headphones className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Membership Support</h1>
        </div>
        <p className="text-slate-600 mt-2">
          Communicate directly with the Membership Team. Send requests, ask questions, or get support.
        </p>
      </motion.div>

      {/* Contact Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-r from-[#002249] to-[#457B9D] p-5 rounded-sm mb-6 text-white"
        data-testid="support-contact-info"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-bold">Need immediate assistance?</h2>
            <p className="text-white/80 text-sm mt-1">Our membership team is here to help you.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 text-sm">
            <a href="tel:1-866-310-3380" className="flex items-center text-white/90 hover:text-white">
              <Headphones className="w-4 h-4 mr-2" /> 1-866-310-3380
            </a>
            <a href="mailto:info@dominicanchamberfl.org" className="flex items-center text-white/90 hover:text-white">
              <Mail className="w-4 h-4 mr-2" /> info@dominicanchamberfl.org
            </a>
          </div>
        </div>
      </motion.div>

      {/* View Toggle */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => { setView('list'); setSelectedThread(null); }}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${view === 'list' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="view-messages"
        >
          <Inbox className="w-4 h-4 inline mr-1" />
          Messages ({messages.length})
        </button>
        <button
          onClick={() => setView('new')}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all flex items-center ${view === 'new' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="new-message-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Message
        </button>
      </div>

      {/* New Message Form */}
      {view === 'new' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#002249]">Send Message to Membership Team</h2>
              <p className="text-sm text-slate-500 mt-1">Your message will go directly to the membership team. They will respond as soon as possible.</p>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="support-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Subject <span className="text-[#E63946]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={e => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="What is your message about?"
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="support-subject-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Message <span className="text-[#E63946]">*</span>
                </label>
                <textarea
                  value={formData.content}
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Type your message here. Be as detailed as possible so we can assist you effectively..."
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[180px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="support-content-input"
                />
              </div>
              <div className="pt-2 flex space-x-3">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setView('list')} data-testid="cancel-support-btn">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="send-support-btn">
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Message Thread View */}
      {view === 'thread' && selectedThread && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button
            onClick={() => { setView('list'); setSelectedThread(null); }}
            className="flex items-center text-sm text-slate-600 hover:text-[#002249] mb-4"
            data-testid="back-to-messages-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to messages
          </button>
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#002249]">{selectedThread.subject}</h2>
              <p className="text-sm text-slate-500">{selectedThread.messages.length} message(s)</p>
            </div>
            <div className="divide-y divide-slate-100">
              {selectedThread.messages.map((msg, index) => {
                const isSent = msg.sender_id === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`p-4 ${isSent ? 'bg-white' : 'bg-blue-50/50'}`}
                    data-testid={`thread-message-${index}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isSent ? 'bg-[#002249]' : 'bg-[#E63946]'}`}>
                          {isSent ? user?.name?.charAt(0) : (msg.sender?.name?.charAt(0) || 'T')}
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-[#002249]">
                            {isSent ? 'You' : (msg.sender?.name || 'Membership Team')}
                          </span>
                          {!isSent && <span className="ml-2 text-xs bg-[#E63946]/10 text-[#E63946] px-1.5 py-0.5 rounded-sm">Staff</span>}
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap pl-10">{msg.content}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Messages List */}
      {view === 'list' && (
        <div className="space-y-3" data-testid="messages-list">
          {threadList.length > 0 ? threadList.map((thread, index) => (
            <motion.div
              key={thread.subject}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * index }}
              className={`bg-white border rounded-sm p-4 cursor-pointer hover:shadow-md transition-all ${thread.hasUnread ? 'border-[#002249] bg-blue-50/30' : 'border-slate-200'}`}
              onClick={() => {
                setSelectedThread(thread);
                setView('thread');
                // Mark unread messages as read
                thread.messages.filter(m => !m.is_read && m.recipient_id === user?.id).forEach(m => markAsRead(m.id));
              }}
              data-testid={`message-thread-${index}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${thread.hasUnread ? 'bg-[#002249]' : 'bg-slate-100'}`}>
                    <MessageSquare className={`w-5 h-5 ${thread.hasUnread ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className={`text-sm truncate ${thread.hasUnread ? 'font-bold text-[#002249]' : 'font-medium text-slate-700'}`}>
                        {thread.subject}
                      </h3>
                      {thread.hasUnread && (
                        <span className="w-2 h-2 bg-[#E63946] rounded-full flex-shrink-0"></span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate mt-0.5">
                      {thread.lastMessage.content}
                    </p>
                    <div className="flex items-center space-x-2 mt-1.5 text-xs text-slate-400">
                      <span>{thread.messages.length} message(s)</span>
                      <span>·</span>
                      <span>{new Date(thread.lastMessage.created_at).toLocaleDateString()}</span>
                      {thread.lastMessage.sender_id !== user?.id && (
                        <>
                          <span>·</span>
                          <span className="text-[#E63946]">Reply from staff</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )) : (
            <div className="text-center py-16 text-slate-500">
              <Headphones className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No messages yet</p>
              <p className="text-sm mt-1">Send a message to the membership team for any questions or support.</p>
              <Button className="mt-4 bg-[#002249] hover:bg-[#001630] rounded-sm" onClick={() => setView('new')} data-testid="send-first-message-btn">
                <Plus className="w-4 h-4 mr-2" /> Send First Message
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalSupport;
